import React from 'react';

import { useShortlistState, useShortlistUIState, useTripsState } from '#states/hooks.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Switch,
} from '#ui';
import { Button } from '#ui/button.js';
import { EmptyState } from '#ui/EmptyState.js';
import { EmptyShortlistIllustration } from '#ui/illustrations/empty-shortlist.js';
import { Input } from '#ui/input.js';
import Loader from '#ui/loader.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { SegmentedControl } from '#ui/segmented-control.js';
import { ShortlistBoardView } from '#ui/shortlist/ShortlistBoardView.js';
import { ShortlistMapView } from '#ui/shortlist/ShortlistMapView.js';

/**
 * Shortlist tab content - displays saved recommendations for the trip
 */
export function ShortlistView(): React.ReactNode {
  const shortlistState = useShortlistState();
  const uiState = useShortlistUIState();
  const tripsState = useTripsState();

  const groups = shortlistState.groups;
  const isReady = shortlistState.isReady;
  const totalCount = shortlistState.totalCount;
  const isCreatingGroup = shortlistState.isCreatingGroup;

  const handleCreateGroup = async () => {
    if (!uiState.newGroupName.trim()) return;
    await shortlistState.createGroup(uiState.newGroupName.trim());
    uiState.closeCreateGroupDialog();
  };

  // Loading state
  if (!isReady) {
    return (
      <div className="h-full w-full flex flex-col justify-center items-center p-6">
        <Loader loading={true} />
      </div>
    );
  }

  // Empty state - no groups means no items have been added yet
  if (totalCount === 0) {
    return (
      <div className="h-full w-full flex flex-col justify-center items-center p-6">
        <EmptyState
          title="Shortlist"
          description="Hit the save button to add hotel, flight, and activity recommendations to your shortlist."
          illustration={<EmptyShortlistIllustration width={198} height={140} />}
        />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 @xl:gap-4 p-4">
        <div className="flex items-center justify-between gap-2 @xl:gap-4 w-full overflow-hidden">
          {/* View mode toggle */}
          <SegmentedControl
            options={[
              { value: 'board', label: 'List', icon: tripsState.isMobile ? 'view_agenda' : 'view_kanban' },
              { value: 'map', label: 'Map', icon: 'map' },
            ]}
            value={uiState.viewMode}
            onValueChange={(value) => uiState.setViewMode(value as 'board' | 'map')}
            ariaLabel="View mode"
          />

          {uiState.viewMode === 'board' && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="show-totals"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Show totals
              </label>
              <Switch
                checked={shortlistState.showTotals}
                onCheckedChange={() => shortlistState.toggleShowTotals()}
                id="show-totals"
              />
            </div>
          )}

          {uiState.viewMode === 'map' && (
            <div className="flex items-center gap-1 max-w-full overflow-hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={uiState.mapGroupFilter ? 'default' : 'ghost'}
                    size="sm"
                    className="max-w-full @xl:w-auto"
                  >
                    <MaterialIcon
                      icon={uiState.mapGroupFilter ? 'filter_list' : 'filter_list_off'}
                      size={IconSizes.sm}
                    />
                    <span className="hidden @xl:inline truncate">
                      {uiState.mapGroupFilter
                        ? (groups.find((g) => g.id === uiState.mapGroupFilter)?.displayName ?? 'Filter by shortlist')
                        : 'All shortlists'}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuRadioGroup
                    value={uiState.mapGroupFilter ?? 'all'}
                    onValueChange={(value) => uiState.setMapGroupFilter(value === 'all' ? null : value)}
                  >
                    <DropdownMenuRadioItem value="all">All shortlists</DropdownMenuRadioItem>
                    <DropdownMenuSeparator />
                    {groups.map((group) => (
                      <DropdownMenuRadioItem key={group.id} value={group.id}>
                        {group.displayName}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

              {uiState.mapGroupFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden flex-shrink-0 @xl:block"
                  onClick={() => uiState.setMapGroupFilter(null)}
                >
                  Reset
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Create group button */}
        <Dialog
          open={uiState.createGroupOpen}
          onOpenChange={(open) => (open ? uiState.openCreateGroupDialog() : uiState.closeCreateGroupDialog())}
        >
          <div className="flex gap-2">
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="hidden @xl:inline-flex">
                <MaterialIcon icon="add" size={IconSizes.sm} />
                New shortlist
              </Button>
            </DialogTrigger>

            <DialogTrigger asChild>
              <Button variant="icon-outline" size="icon-sm" className="@xl:hidden">
                <MaterialIcon icon="add" size={IconSizes.sm} />
              </Button>
            </DialogTrigger>
          </div>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create shortlist</DialogTitle>
              <DialogDescription>Create a shortlist to organize your favorite recommendations.</DialogDescription>
            </DialogHeader>
            <Input
              placeholder="Shortlist name"
              value={uiState.newGroupName}
              onChange={(e) => uiState.setNewGroupName(e.target.value)}
              onKeyDown={async (e) => e.key === 'Enter' && handleCreateGroup()}
              autoFocus
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => uiState.closeCreateGroupDialog()}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateGroup}
                isBusy={isCreatingGroup}
                disabled={!uiState.newGroupName.trim() || isCreatingGroup}
              >
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Content - Conditional rendering based on view mode */}
      {uiState.viewMode === 'board' ? <ShortlistBoardView /> : <ShortlistMapView />}
    </div>
  );
}
